import { useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import { Keyboard } from "@capacitor/keyboard";

// Keeps any open Radix Dialog visible above the iOS keyboard.
//
// Why we mutate inline `top` directly instead of relying on a CSS variable:
//   - iOS WebKit doesn't always reflow `calc(var(--x))` values when --x changes
//     mid-keyboard-animation, so a CSS-variable approach is intermittent.
//   - Setting inline `top` on the open dialog element bypasses Tailwind utilities
//     and any reflow timing — it's deterministic.
//
// Capacitor `KeyboardResize.None` keeps the WebView at full screen size so the
// math (`top = 50% - keyboardHeight / 2`) is always correct.
export function useKeyboardOffset() {
  useEffect(() => {
    const platform = Capacitor.getPlatform();
    const isAndroid = platform === "android";

    const openDialogs = (): HTMLElement[] =>
      Array.from(
        document.querySelectorAll<HTMLElement>(
          '[role="dialog"][data-state="open"]'
        )
      );

    const applyKbOffset = (kbHeight: number) => {
      const root = document.documentElement;
      if (!isAndroid) {
        if (kbHeight > 0) {
          root.style.setProperty("--kb-h", `${kbHeight}px`);
        } else {
          root.style.removeProperty("--kb-h");
        }
      }
      if (isAndroid) return;
      const top = kbHeight > 0 ? `calc(50% - ${kbHeight}px / 2)` : "";
      for (const d of openDialogs()) {
        d.style.top = top;
      }
    };

    if (Capacitor.isNativePlatform()) {
      const willShow = Keyboard.addListener("keyboardWillShow", (info) => {
        applyKbOffset(info.keyboardHeight);
      });
      const didShow = Keyboard.addListener("keyboardDidShow", (info) => {
        applyKbOffset(info.keyboardHeight);
      });
      const willHide = Keyboard.addListener("keyboardWillHide", () => {
        applyKbOffset(0);
      });

      // When a dialog opens AFTER the keyboard is already up, the new dialog
      // needs the offset applied to its inline style. A MutationObserver on
      // body catches that case.
      const observer = !isAndroid
        ? new MutationObserver(() => {
            const kb = parseInt(
              getComputedStyle(document.documentElement).getPropertyValue(
                "--kb-h"
              ),
              10
            );
            if (!isNaN(kb) && kb > 0) {
              for (const d of openDialogs()) {
                if (!d.style.top) d.style.top = `calc(50% - ${kb}px / 2)`;
              }
            }
          })
        : null;
      observer?.observe(document.body, { childList: true, subtree: true });

      return () => {
        willShow.then((l) => l.remove()).catch(() => {});
        didShow.then((l) => l.remove()).catch(() => {});
        willHide.then((l) => l.remove()).catch(() => {});
        observer?.disconnect();
        applyKbOffset(0);
      };
    }

    const vv = window.visualViewport;
    if (!vv) return;
    const onResize = () =>
      applyKbOffset(Math.max(0, window.innerHeight - vv.height));
    vv.addEventListener("resize", onResize);
    return () => {
      vv.removeEventListener("resize", onResize);
      applyKbOffset(0);
    };
  }, []);
}
